import { DATABASE_CONFIG } from "./config";
import { DatabaseFilesystemKind } from "@/domains/database/types/db";
import { logger } from "@/modules/logger";

const PGLITE_PREFIX = "/pglite";

type IndexedDbWithDatabases = IDBFactory & {
  databases?: () => Promise<{ name?: string | null }[]>;
};

/**
 * Given a list of IndexedDB database names, return those that should be deleted
 * for the current schema version. A name is considered obsolete if:
 * - it starts with `${NAME_PREFIX}-`, and
 * - it does not start with `${NAME_PREFIX}-${DATABASE_VERSION}-`, and
 * - it is not the current database name.
 */
export function getObsoleteDatabaseNames({
  allNames,
  currentDatabaseName,
}: {
  allNames: string[];
  currentDatabaseName: string;
}): string[] {
  const basePrefix = `${PGLITE_PREFIX}/${DATABASE_CONFIG.NAME_PREFIX}-`;
  const versionPrefix = `${PGLITE_PREFIX}/${DATABASE_CONFIG.NAME_PREFIX}-${DATABASE_CONFIG.DATABASE_VERSION}-`;

  return allNames.filter(name => {
    if (name === currentDatabaseName) return false;
    if (!name.startsWith(basePrefix)) return false;

    // Keep any DBs that match the current schema version prefix
    if (name.startsWith(versionPrefix)) return false;

    return true;
  });
}

/**
 * Best-effort cleanup of obsolete per-account databases before opening the current one.
 * Only runs in IndexedDB environments that support database enumeration.
 * Returns a list of names that were deleted.
 */
export async function cleanupObsoleteDatabases({
  currentDatabaseName,
  filesystemKind,
  idbFactory,
}: {
  currentDatabaseName: string;
  filesystemKind: DatabaseFilesystemKind;
  idbFactory?: IDBFactory;
}): Promise<string[]> {
  // Only relevant for IndexedDB; skip otherwise or if not in browser
  if (filesystemKind !== DatabaseFilesystemKind.IndexedDB || typeof indexedDB === "undefined") {
    return [];
  }

  const deletedNames: string[] = [];

  try {
    const idb = (idbFactory ?? indexedDB) as unknown as IndexedDbWithDatabases;

    if (typeof idb.databases !== "function") {
      logger.debug({
        message:
          "[DatabaseConnectionManager] Skipping obsolete DB cleanup (no enumeration support)",
      });

      return [];
    }

    const dbInfos = await idb.databases();
    const names = (dbInfos ?? []).map(info => info.name).filter((n): n is string => !!n);
    const toDelete = getObsoleteDatabaseNames({ allNames: names, currentDatabaseName });

    // Delete databases in parallel
    await Promise.allSettled(
      toDelete.map(async name => {
        try {
          await new Promise<void>((resolve, reject) => {
            const req = idb.deleteDatabase(name);
            req.onsuccess = () => resolve();
            req.onerror = req.onblocked = () => reject(new Error(`Failed to delete ${name}`));
          });

          logger.info({
            message: "[DatabaseConnectionManager] Deleted obsolete database",
            info: { name },
          });
          deletedNames.push(name);
        } catch (error) {
          logger.warn({
            message: "[DatabaseConnectionManager] Failed to delete obsolete database",
            info: { name, error: error instanceof Error ? error.message : String(error) },
          });
        }
      })
    );
  } catch (error) {
    logger.warn({
      message: "[DatabaseConnectionManager] Obsolete DB cleanup encountered an error",
      info: { error: error instanceof Error ? error.message : String(error) },
    });
  }

  return deletedNames;
}
